import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { safeHtml } from '../utils/safeHtml';
import { uploadMedia } from '../utils/uploadMedia';

// Inline Image Icon for the upload button
const ImageIcon = ({ size = 24, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
);

const CommsView = ({ data, userData, isAdmin, showToast, localComms, setLocalComms, crewMode = false }) => {
    const { handle, clearance } = userData;
    const [message, setMessage] = useState('');
    const [activeThreadId, setActiveThreadId] = useState(null);
    const [showNewThread, setShowNewThread] = useState(false);
    const [newThreadTitle, setNewThreadTitle] = useState('');
    const [newThreadShared, setNewThreadShared] = useState(false); // admins: let crew (artists, musicians, writers, helpers) see and join it
    const [editingMsgId, setEditingMsgId] = useState(null); 
    const [isUploading, setIsUploading] = useState(false); // Track image upload status
    const messagesEndRef = useRef(null);

    const commsData = [...(data?.comms || []), ...localComms];
    const canCreateThread = isAdmin || ['LEVEL 4', 'LEVEL 5', 'NOSOVEREIGN', 'PILLAR'].includes(clearance?.toUpperCase());

    const allMessages = commsData.filter(d => d.type === 'message');
    const rawThreads = commsData.filter(d => d.type === 'thread');

    // NEW: Format Date and Time
    const formatDateTime = (ts) => {
        if (!ts) return 'Just now';
        try {
            const ms = ts.toMillis ? ts.toMillis() : (ts.seconds ? ts.seconds * 1000 : Date.now());
            const d = new Date(ms);
            return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + 
                   d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch(e) {
            return 'Just now';
        }
    };

    const sortedThreads = useMemo(() => {
        const builtThreads = rawThreads.map(t => {
            const msgs = allMessages.filter(m => m.threadId === t.id).sort((a, b) => (b.timestamp?.toMillis() || 0) - (a.timestamp?.toMillis() || 0));
            const rawText = msgs[0]?.text ? String(msgs[0].text).replace(/<[^>]*>?/gm, '') : 'No transmissions yet.';
            
            const lastViewedThisThread = parseInt(localStorage.getItem(`ts_viewed_thread_${t.id}`) || '0', 10);
            const latestMsgTime = msgs[0]?.timestamp?.toMillis ? msgs[0].timestamp.toMillis() : 0;
            const hasUnread = latestMsgTime > lastViewedThisThread;

            return { ...t, msgCount: msgs.length, lastMsgText: rawText, hasUnread };
        });

        const genMsgs = allMessages.filter(m => !m.threadId || m.threadId === 'general').sort((a, b) => (b.timestamp?.toMillis() || 0) - (a.timestamp?.toMillis() || 0));
        const genRawText = genMsgs[0]?.text ? String(genMsgs[0].text).replace(/<[^>]*>?/gm, '') : 'No transmissions yet.';
        
        const lastViewedGen = parseInt(localStorage.getItem(`ts_viewed_thread_general`) || '0', 10);
        const latestGenTime = genMsgs[0]?.timestamp?.toMillis ? genMsgs[0].timestamp.toMillis() : 0;

        if (!crewMode) builtThreads.push({
            id: 'general', title: 'General Operations', handle: 'SYSTEM', isPinned: true, lastActivity: { toMillis: () => Infinity },
            msgCount: genMsgs.length, lastMsgText: genRawText, hasUnread: latestGenTime > lastViewedGen
        });

        return builtThreads.sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            const tA = a.lastActivity?.toMillis ? a.lastActivity.toMillis() : (a.timestamp?.toMillis ? a.timestamp.toMillis() : 0);
            const tB = b.lastActivity?.toMillis ? b.lastActivity.toMillis() : (b.timestamp?.toMillis ? b.timestamp.toMillis() : 0);
            return tB - tA;
        });
    }, [rawThreads, allMessages, crewMode]);

    const activeMessages = useMemo(() => {
        if (!activeThreadId) return [];
        const filtered = activeThreadId === 'general' 
            ? allMessages.filter(m => !m.threadId || m.threadId === 'general')
            : allMessages.filter(m => m.threadId === activeThreadId);
        return filtered.sort((a, b) => (a.timestamp?.toMillis() || 0) - (b.timestamp?.toMillis() || 0));
    }, [allMessages, activeThreadId]);
    
    useEffect(() => { 
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); 
    }, [activeMessages]);

    const handleOpenThread = (id) => {
        setActiveThreadId(id);
        localStorage.setItem(`ts_viewed_thread_${id}`, Date.now().toString());
    };

    const handleCreateThread = async (e) => {
        e.preventDefault();
        if (!newThreadTitle.trim()) return;
        try {
            const docRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'comms'), {
                type: 'thread', title: newThreadTitle, handle: handle || 'Operative', isPinned: false, timestamp: serverTimestamp(), lastActivity: serverTimestamp(),
                ...(newThreadShared ? { shared: true } : {})
            });
            setNewThreadTitle(''); setNewThreadShared(false); setShowNewThread(false); handleOpenThread(docRef.id);
            showToast("Secure channel established.", "success");
        } catch(err) { 
            console.warn(err);
            const tempId = 'local_thread_' + Date.now();
            setLocalComms(prev => [...prev, { id: tempId, type: 'thread', title: newThreadTitle, handle: handle || 'Operative', timestamp: { toMillis: () => Date.now() }, lastActivity: { toMillis: () => Date.now() } }]);
            setNewThreadTitle(''); setShowNewThread(false); handleOpenThread(tempId);
            showToast("Saved channel locally (Offline).", "success");
        }
    };

    // --- PHOTO UPLOAD LOGIC ---
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        showToast("Uploading image to server...", "success");

        try {
            const url = await uploadMedia(file);
            // Insert the image into the Quill editor HTML
            const imgTag = `<br><img src="${url}" alt="Attached Image" /><br>`;
            setMessage(prev => prev + imgTag);
            showToast("Image attached!", "success");
        } catch (error) {
            console.error("Upload Error:", error);
            showToast("Upload failed: " + (error.message || "check your connection."), "error");
        } finally {
            setIsUploading(false);
            e.target.value = null; // reset input
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        const plainText = message.replace(/<[^>]*>?/gm, '').trim();
        // Allow sending if there's text OR if there's an image attached
        if (!plainText && !message.includes('<img')) return;
        if (!activeThreadId) return;

        try {
            if (editingMsgId) {
                if (editingMsgId.startsWith('local_')) {
                    setLocalComms(prev => prev.map(m => m.id === editingMsgId ? { ...m, text: message, isEdited: true } : m));
                } else {
                    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'comms', editingMsgId), {
                        text: message,
                        isEdited: true
                    });
                }
                setEditingMsgId(null);
            } else {
                if (activeThreadId.startsWith('local_')) {
                    setLocalComms(prev => [...prev, { id: 'local_msg_' + Date.now(), type: 'message', threadId: activeThreadId, handle: handle || 'Operative', text: message, timestamp: { toMillis: () => Date.now() } }]);
                } else {
                    const inSharedThread = activeThreadId !== 'general' && !!(rawThreads.find(t => t.id === activeThreadId) || {}).shared;
                    await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'comms'), {
                        type: 'message', threadId: activeThreadId, handle: handle || 'Operative', text: message, timestamp: serverTimestamp(),
                        ...(inSharedThread ? { shared: true } : {})
                    });
                    if (activeThreadId !== 'general') {
                        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'comms', activeThreadId), { lastActivity: serverTimestamp() });
                    }
                }
            }
            setMessage('');
            localStorage.setItem(`ts_viewed_thread_${activeThreadId}`, Date.now().toString());
        } catch(err) { showToast("Firebase Error.", "error"); }
    };

    const triggerEdit = (msg) => { setEditingMsgId(msg.id); setMessage(msg.text); };
    const cancelEdit = () => { setEditingMsgId(null); setMessage(''); };

    const handleTogglePin = async (e, thread) => {
        e.stopPropagation();
        if (thread.id === 'general') return;
        try { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'comms', thread.id), { isPinned: !thread.isPinned }); } 
        catch(err) { showToast("Permission denied.", "error"); }
    };

    const handleDelete = async (id, isThread = false) => {
        if (!window.confirm("Purge transmission?")) return;
        try {
            if (isThread) {
                const toDelete = commsData.filter(d => d.id === id || d.threadId === id);
                toDelete.forEach(d => {
                    if (d.id.startsWith('local_')) setLocalComms(prev => prev.filter(x => x.id !== d.id));
                    else deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'comms', d.id)).catch(()=>{});
                });
                setActiveThreadId(null);
            } else {
                if (id.startsWith('local_')) setLocalComms(prev => prev.filter(x => x.id !== id));
                else await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'comms', id)); 
                if (editingMsgId === id) cancelEdit();
            }
            showToast("Purged.", "success");
        } catch(e) { showToast("Permission denied.", "error"); }
    };

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean']
        ],
    };

    if (!activeThreadId) {
        return (
            <div className="flex-1 flex flex-col h-full bg-[#050505] p-6 overflow-hidden fade-in pb-24">
                <div className="flex justify-between items-center mb-6 border-b border-gray-900 pb-4 shrink-0">
                    <h2 className="text-xl font-bold flex items-center gap-2 text-white font-sans tracking-tight">
                        <Icons.Mail className="text-blue-500" /> SECURE COMMS
                    </h2>
                    {canCreateThread && (
                        <button onClick={() => setShowNewThread(!showNewThread)} className="bg-blue-900/20 text-blue-400 border border-blue-900/50 px-4 py-2 rounded text-[10px] uppercase font-bold tracking-widest hover:bg-blue-900 hover:text-white transition-colors flex items-center gap-2">
                            <Icons.Plus size={14} /> NEW GROUP CHAT
                        </button>
                    )}
                </div>

                {showNewThread && (
                    <form onSubmit={handleCreateThread} className="mb-6 p-6 bg-[#0a0a0a] border border-blue-900/50 rounded flex flex-wrap gap-4 items-center fade-in shrink-0 shadow-lg">
                        <input type="text" spellCheck={true} value={newThreadTitle} onChange={e=>setNewThreadTitle(e.target.value)} placeholder="Enter Chat Subject..." className="flex-1 min-w-[200px] bg-[#111] border border-gray-800 p-3 text-white text-sm focus:border-blue-600 outline-none font-bold" autoFocus />
                        <label className="flex items-center gap-2 text-[11px] text-gray-300 font-bold cursor-pointer" title="Crew (artists, musicians, writers, helpers) can read and post in this chat">
                            <input type="checkbox" checked={newThreadShared} onChange={e => setNewThreadShared(e.target.checked)} className="accent-green-500" /> Share with crew
                        </label>
                        <button type="button" onClick={() => setShowNewThread(false)} className="text-gray-500 hover:text-white text-xs font-bold uppercase px-4">Cancel</button>
                        <button type="submit" disabled={!newThreadTitle.trim()} className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 text-xs font-bold uppercase tracking-widest disabled:opacity-50 transition-colors rounded">Create</button>
                    </form>
                )}

                <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-2 pr-2">
                    {sortedThreads.map(t => (
                        <div key={t.id} onClick={() => handleOpenThread(t.id)} className={`bg-[#0a0a0a] border p-5 rounded transition-colors cursor-pointer group flex items-center shadow-lg relative ${t.isPinned ? 'border-blue-900/50' : 'border-gray-900 hover:border-gray-700'}`}>
                            
                            <div className="w-6 flex shrink-0 justify-center">
                                {t.hasUnread && <div className="w-3 h-3 bg-[#00ff41] rounded-full animate-pulse shadow-[0_0_8px_rgba(0,255,65,0.8)]"></div>}
                            </div>

                            <div className="flex-1 flex flex-col justify-center ml-2">
                                <div className="flex items-center gap-3 mb-1">
                                    {t.isPinned && <Icons.MapPin size={12} className="text-blue-500 shrink-0" />}
                                    <h3 className={`font-bold text-lg leading-none ${t.hasUnread ? 'text-white' : 'text-gray-300'}`}>{t.title}</h3>
                                    <span className="text-[9px] text-gray-600 font-mono border border-gray-800 rounded px-1.5 py-0.5">{t.msgCount} MSGS</span>
                                    {t.shared && <span className="text-[9px] text-green-400 font-mono border border-green-900/60 bg-green-900/10 rounded px-1.5 py-0.5">CREW</span>}
                                </div>
                                <div className={`text-xs font-sans line-clamp-1 leading-relaxed ${t.hasUnread ? 'text-gray-300' : 'text-gray-500'}`}>
                                    <span className="font-bold mr-2 uppercase tracking-widest text-[9px]">{t.handle}:</span> {t.lastMsgText}
                                </div>
                            </div>

                            <div className="flex items-center gap-4 shrink-0">
                                {isAdmin && t.id !== 'general' && (
                                    <div className="hidden group-hover:flex gap-2">
                                        <button onClick={(e) => handleTogglePin(e, t)} className={`p-2 rounded bg-black border transition-colors ${t.isPinned ? 'border-blue-500 text-blue-500' : 'border-gray-800 text-gray-500 hover:text-blue-400'}`} title="Pin Thread"><Icons.MapPin size={14}/></button>
                                        <button onClick={(e) => { e.stopPropagation(); handleDelete(t.id, true); }} className="p-2 bg-black border border-gray-800 rounded text-gray-500 hover:text-red-500 transition-colors" title="Purge Thread"><Icons.Trash2 size={14}/></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col h-full bg-[#050505] p-6 overflow-hidden fade-in pb-24">
            
            <style>{`
                .quill-dark .ql-toolbar { background: #111; border-color: #333; border-top-left-radius: 4px; border-top-right-radius: 4px; }
                .quill-dark .ql-container { background: #0a0a0a; border-color: #333; border-bottom-left-radius: 4px; border-bottom-right-radius: 4px; font-family: 'Inter', sans-serif; font-size: 14px; }
                .quill-dark .ql-editor { min-height: 80px; max-height: 200px; overflow-y: auto; }
                .quill-dark .ql-stroke { stroke: #aaa; }
                .quill-dark .ql-fill { fill: #aaa; }
                .quill-dark .ql-picker { color: #aaa; }
                .quill-dark .ql-editor.ql-blank::before { color: #555; font-style: italic; }
                
                /* CHAT HTML FORMATTING */
                .rich-text-content a { color: #3b82f6; text-decoration: underline; }
                .rich-text-content blockquote { border-left: 4px solid #333; padding-left: 1rem; color: #aaa; font-style: italic; margin: 1rem 0; }
                .rich-text-content pre { background: #111; padding: 1rem; border-radius: 4px; font-family: monospace; overflow-x: auto; }
                .rich-text-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
                .rich-text-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
                /* Beautiful inline images */
                .rich-text-content img { max-width: 100%; height: auto; border-radius: 8px; margin-top: 8px; border: 1px solid #333; cursor: zoom-in; }
            `}</style>

            <div className="flex items-center gap-4 mb-6 border-b border-gray-900 pb-4 shrink-0">
                <button onClick={() => { setActiveThreadId(null); cancelEdit(); }} className="text-gray-500 hover:text-blue-400 transition-colors p-2 bg-[#0a0a0a] border border-gray-800 rounded flex items-center gap-2 text-xs font-bold uppercase tracking-widest"><Icons.ArrowLeft size={16} /> Back</button>
                <div>
                    <h2 className="text-xl font-bold text-white font-sans tracking-tight">{sortedThreads.find(t => t.id === activeThreadId)?.title || 'Unknown Thread'}</h2>
                    <p className="text-[10px] text-blue-500 uppercase tracking-widest font-bold">GROUP CHAT</p>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar pr-4 flex flex-col gap-4 mb-4">
                {activeMessages.length === 0 && <div className="text-gray-600 font-mono text-sm uppercase tracking-widest text-center mt-10 border border-dashed border-gray-800 p-10">NO TRANSMISSIONS LOGGED IN THIS CHANNEL.</div>}
                
                {activeMessages.map((msg, index) => {
                    const isMe = msg.handle === handle;
                    const canEdit = isAdmin || isMe;
                    const prevMsg = index > 0 ? activeMessages[index - 1] : null;
                    const isSameAuthorAsPrev = prevMsg && prevMsg.handle === msg.handle;
                    
                    return (
                        <div key={msg.id} className={`flex flex-col w-full ${isMe ? 'items-end' : 'items-start'} ${isSameAuthorAsPrev ? 'mt-0' : 'mt-4'} group`}>
                            {!isSameAuthorAsPrev && (
                                <span className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isMe ? 'text-blue-500' : 'text-gray-400'}`}>
                                    {msg.handle} <span className="text-gray-600 font-mono font-normal ml-2 lowercase tracking-normal">{formatDateTime(msg.timestamp)}</span>
                                </span>
                            )}
                            <div className={`p-4 rounded-xl flex flex-col w-fit max-w-[90%] sm:max-w-[70%] border ${isMe ? 'bg-blue-900/20 border-blue-900/50 rounded-tr-none' : 'bg-[#111] border-gray-800 rounded-tl-none'}`}>
                                <div className="text-sm text-gray-200 font-sans leading-relaxed rich-text-content" dangerouslySetInnerHTML={{ __html: safeHtml(msg.text) }} />
                                
                                <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-800/50 min-w-[120px]">
                                    <span className="text-[8px] text-gray-600 uppercase font-mono">
                                        {msg.isEdited ? '(Edited)' : ''}
                                    </span>
                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {canEdit && <button onClick={()=>triggerEdit(msg)} className="text-gray-500 hover:text-white"><Icons.Edit2 size={10}/></button>}
                                        {canEdit && <button onClick={()=>handleDelete(msg.id)} className="text-gray-500 hover:text-red-500"><Icons.Trash2 size={10}/></button>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            <div className="shrink-0 border-t border-gray-900 pt-6 flex flex-col gap-2">
                {editingMsgId && (
                    <div className="flex justify-between items-center bg-blue-900/20 border border-blue-900/50 text-blue-400 text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded">
                        <span>Editing Message...</span>
                        <button onClick={cancelEdit} className="hover:text-white">Cancel</button>
                    </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-4 items-end">
                    <div className="flex-1 w-full quill-dark">
                        <ReactQuill 
                            theme="snow" 
                            value={message} 
                            onChange={setMessage} 
                            modules={modules}
                            placeholder={`Message ${sortedThreads.find(t => t.id === activeThreadId)?.title}...`}
                        />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto h-[44px]">
                        <label className="flex items-center justify-center px-4 bg-gray-900 border border-gray-700 text-gray-400 hover:text-white rounded cursor-pointer transition-colors" title="Attach Image">
                            {isUploading ? <Icons.Activity className="animate-spin" size={18} /> : <ImageIcon size={18} />}
                            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={isUploading} />
                        </label>
                        <button 
                            onClick={handleSend} 
                            disabled={isUploading || (!message.replace(/<[^>]*>?/gm, '').trim() && !message.includes('<img'))} 
                            className="bg-blue-600 text-white px-8 font-black tracking-widest hover:bg-blue-500 disabled:opacity-30 uppercase text-xs transition-colors rounded h-full"
                        >
                            {editingMsgId ? 'UPDATE' : 'SEND'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommsView;